import React from 'react';
import { Mail, Phone, MapPin, ShieldCheck, Truck, Headphones, RefreshCw } from 'lucide-react';

export default function Footer({
    setActiveCategory,
    setCurrentPage,
    adminProducts = [],
    pujaProducts = [],
    herbalProducts = [],
    jaivikProducts = [],
    ayurvedicProducts = [],
    organicProducts = [],
    onViewDetails
}) {
    // Single-click fix ke liye e.stopPropagation() aur e.preventDefault() add kiya gaya hai
    const handleFooterNav = (page, category = 'all', e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (typeof setActiveCategory === 'function') {
            setActiveCategory(category);
        }
        if (typeof setCurrentPage === 'function') {
            setCurrentPage(page);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleProductClick = (product, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (product && typeof onViewDetails === 'function') {
            onViewDetails(product);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const allFooterProducts = [
        ...(adminProducts || []),
        ...(pujaProducts || []),
        ...(herbalProducts || []),
        ...(jaivikProducts || []),
        ...(ayurvedicProducts || []),
        ...(organicProducts || [])
    ];

    const uniqueProducts = Array.from(new Set(allFooterProducts.map(p => p.id || p.name)))
        .map(id => allFooterProducts.find(p => (p.id || p.name) === id));

    return (
        <footer className="bg-gray-900 text-gray-300 pt-16 pb-8 border-t border-gray-800">
            <div className="max-w-7xl mx-auto px-4">

                {/* Top Trust/Features Bar */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pb-12 border-b border-gray-800">
                    <div className="flex items-center gap-4">
                        <div className="bg-orange-600/10 p-3 rounded-xl text-orange-500 border border-orange-500/20">
                            <Truck size={24} />
                        </div>
                        <div>
                            <h4 className="font-bold text-white text-sm">Fast Delivery</h4>
                            <p className="text-xs text-gray-400">Doorstep delivery across India</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="bg-orange-600/10 p-3 rounded-xl text-orange-500 border border-orange-500/20">
                            <ShieldCheck size={24} />
                        </div>
                        <div>
                            <h4 className="font-bold text-white text-sm">100% Pure & Certified</h4>
                            <p className="text-xs text-gray-400">Lab tested organic & spiritual goods</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="bg-orange-600/10 p-3 rounded-xl text-orange-500 border border-orange-500/20">
                            <RefreshCw size={24} />
                        </div>
                        <div>
                            <h4 className="font-bold text-white text-sm">Easy Returns</h4>
                            <p className="text-xs text-gray-400">Hassle-free replacement policy</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="bg-orange-600/10 p-3 rounded-xl text-orange-500 border border-orange-500/20">
                            <Headphones size={24} />
                        </div>
                        <div>
                            <h4 className="font-bold text-white text-sm">24/7 Support</h4>
                            <p className="text-xs text-gray-400">Dedicated customer assistance</p>
                        </div>
                    </div>
                </div>

                {/* Main Footer Links */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 py-12 border-b border-gray-800 text-sm">

                    <div className="lg:col-span-2 space-y-4">
                        <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => handleFooterNav('home', 'all', e)}>
                            <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl p-1 shadow">
                                <img
                                    src="/logo.png"
                                    alt="Logo"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentNode.innerHTML = '<span class="bg-orange-600 text-white font-black text-lg px-2 py-1 rounded-lg">RE</span>';
                                    }}
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <h3 className="text-lg font-extrabold text-white tracking-wide">
                                Rudraksh <span className="text-orange-400">Enterprises</span>
                            </h3>
                        </div>
                        <p className="text-xs text-gray-400 leading-relaxed max-w-sm">
                            Your trusted partner for 100% pure Puja Samagri, Herbal items, Jaivik foods, Ayurvedic formulations, and Organic lifestyle products.
                        </p>

                        {/* Featured Products Links */}
                        {uniqueProducts.length > 0 && (
                            <div className="pt-2">
                                <p className="text-xs font-bold text-white mb-1.5">Featured Products:</p>
                                <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto pr-2">
                                    {uniqueProducts.slice(0, 6).map((prod, idx) => (
                                        <button
                                            key={prod.id || idx}
                                            type="button"
                                            onClick={(e) => handleProductClick(prod, e)}
                                            className="text-[11px] bg-gray-800 hover:bg-orange-600 hover:text-white text-gray-300 px-2.5 py-1 rounded-lg transition border border-gray-700 cursor-pointer border-none"
                                        >
                                            {prod.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Quick Links */}
                    <div className="space-y-3">
                        <h4 className="font-bold text-white text-base">Quick Links</h4>
                        <ul className="space-y-2 text-xs text-gray-400">
                            <li><button onClick={(e) => handleFooterNav('home', 'all', e)} className="hover:text-orange-400 transition bg-transparent border-none cursor-pointer text-gray-400 p-0">Home</button></li>
                            <li><button onClick={(e) => handleFooterNav('category', 'all', e)} className="hover:text-orange-400 transition bg-transparent border-none cursor-pointer text-gray-400 p-0">E-Catalogue</button></li>
                            <li><button onClick={(e) => handleFooterNav('contact', 'all', e)} className="hover:text-orange-400 transition bg-transparent border-none cursor-pointer text-gray-400 p-0">Contact Us</button></li>
                        </ul>
                    </div>

                    {/* Our Categories */}
                    <div className="space-y-3">
                        <h4 className="font-bold text-white text-base">Our Categories</h4>
                        <ul className="space-y-2 text-xs text-gray-400">
                            <li><button onClick={(e) => handleFooterNav('category', 'puja', e)} className="hover:text-orange-400 transition bg-transparent border-none cursor-pointer text-gray-400 p-0">Puja Samagri</button></li>
                            <li><button onClick={(e) => handleFooterNav('category', 'herbal', e)} className="hover:text-orange-400 transition bg-transparent border-none cursor-pointer text-gray-400 p-0">Herbal Products</button></li>
                            <li><button onClick={(e) => handleFooterNav('category', 'jaivik', e)} className="hover:text-orange-400 transition bg-transparent border-none cursor-pointer text-gray-400 p-0">Jaivik Products</button></li>
                            <li><button onClick={(e) => handleFooterNav('category', 'ayurvedic', e)} className="hover:text-orange-400 transition bg-transparent border-none cursor-pointer text-gray-400 p-0">Ayurvedic Products</button></li>
                            <li><button onClick={(e) => handleFooterNav('category', 'organic', e)} className="hover:text-orange-400 transition bg-transparent border-none cursor-pointer text-gray-400 p-0">Organic Products</button></li>
                        </ul>
                    </div>

                    {/* Get in Touch */}
                    <div className="space-y-3">
                        <h4 className="font-bold text-white text-base">Get in Touch</h4>
                        <div className="space-y-2.5 text-xs text-gray-400">
                            <p className="flex items-start gap-2">
                                <MapPin size={16} className="text-orange-400 shrink-0 mt-0.5" />
                                <span>L-325, Sec-23, Sanjay Nagar, Ghaziabad - 201002</span>
                            </p>
                            <p className="flex items-center gap-2">
                                <Phone size={16} className="text-orange-400 shrink-0" />
                                <span>+91 98919 90313, +91 99119 92162</span>
                            </p>
                            <p className="flex items-center gap-2">
                                <Mail size={16} className="text-orange-400 shrink-0" />
                                <span>support@rudrakshenterprises.com</span>
                            </p>
                        </div>
                    </div>

                </div>

                {/* Bottom Copyright & LiveIndiaTech Credit */}
                <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-4">
                    <p>© {new Date().getFullYear()} Rudraksh Enterprises. All rights reserved.</p>
                    <p className="flex items-center gap-1.5 font-medium text-gray-400">
                        Designed & Developed by <a href="https://liveindiatech.com" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline font-bold">LiveIndiaTech</a>
                    </p>
                </div>

            </div>
        </footer>
    );
}